import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { CatcherTimes, Dropdown } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import { usePitchVideoFn } from '../data';

const StyledContainer = styled.div`
    display: inline-block;
`;
const StyledFiltersContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    > div {
        display: flex;
    }
    > div > * {
        margin: 5px;
    }
`;
const StyledFiltersLabel = styled.div`
    font-size: 12px;
    font-weight: bold;
    margin-top: auto;
    margin-bottom: auto;
    padding: 0 10px;
`;

const filterThrows = (throws, base, filters) => {
    const { accuracy } = filters;
    const filteredThrows = throws.filter((t) => t.base === base && (!accuracy.filterFn || accuracy.filterFn(t)));
    return filteredThrows;
};

const makeSeries = (translate, throws) => {
    const series = throws.map((t) => ({
        x: Number(t.throwtime?.toFixed(2)),
        y: Number(t.transfertime?.toFixed(2)),
        name: '',
        tooltip: `${t.type} ${t.base}B, ${t.transfertime}<br />${t.istop ? `${translate('baseball.abbr-top')} ` : `${translate('baseball.abbr-bottom')} `}${t.inning}, ${t.outsbefore} ${translate('baseball.outs', { count: 1 })}, ${t.balls}-${t.strikes}<br />${translate('baseball.runners', { count: 1 })}: ${t.runnername} (${t.battingteamabbr})`,
        url: t.url,
        gameid: t.gameid,
        eventseq: t.eventseq,
        pitchseq: t.pitchseq,
    }));
    return { data: series };
};

const accuracyOptions = (translate) => [
    { name: translate('common.all'), filterFn: null },
    { name: translate('components.catcher-times-chart.great'), filterFn: (x) => x.greatthrow },
    { name: translate('components.catcher-times-chart.good'), filterFn: (x) => x.goodthrow },
    { name: translate('components.catcher-times-chart.poor'), filterFn: (x) => !x.greatthrow && !x.goodthrow },
];

const CatcherTimesChart = ({ base, data, onClick }) => {
    const { t: translate } = useTranslation();
    const [filters, setFilters] = useState({
        accuracy: accuracyOptions(translate)[0],
    });
    const pitchVideoFn = usePitchVideoFn();
    const filteredData = filterThrows(data, base, filters);
    const seriesData = makeSeries(translate, filteredData, base);

    return (
        <>
            <StyledContainer>
                <CatcherTimes
                    titleX={translate('components.catcher-times-chart.throw-time')}
                    titleY={translate('components.catcher-times-chart.exchange-time')}
                    seriesData={seriesData}
                    enableZoom={false}
                    showlegend
                    pitchVideoFn={pitchVideoFn}
                    onClick={(p) => onClick(p)}
                />
            </StyledContainer>
            <StyledFiltersContainer>
                <StyledFiltersLabel>{translate('components.catcher-times-chart.accuracy')}:</StyledFiltersLabel>
                <Dropdown
                    options={accuracyOptions(translate)}
                    value={filters.accuracy}
                    onSelect={(value) => setFilters({ ...filters, accuracy: value })}
                />
            </StyledFiltersContainer>
        </>
    );
};

CatcherTimesChart.propTypes = {
    base: PropTypes.number.isRequired,
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    onClick: PropTypes.func,
};

CatcherTimesChart.defaultProps = {
    onClick: undefined,
};

export default CatcherTimesChart;
